import { useState, useCallback } from 'react'
import { Outlet, useLocation } from 'react-router-dom'

import { DevRolePreviewBanner } from '@/components/shared/DevRolePreviewBanner'
import { Header } from '@/components/shared/Header'
import { Sidebar } from '@/components/shared/Sidebar'
import { useAthenaRecommendationsWs } from '@/lib/hooks/useAthenaRecommendationsWs'
import { useActiveCompanyId } from '@/lib/queries/companies'
import { cn } from '@/lib/utils'

const SIDEBAR_STORAGE_KEY = 'vectraclip-sidebar-collapsed'

function readCollapsed(): boolean {
  try {
    return window.localStorage.getItem(SIDEBAR_STORAGE_KEY) === 'true'
  } catch {
    return false
  }
}

/**
 * Rotas "full-bleed": páginas que querem ocupar 100% da viewport (sem
 * padding interno do main nem max-width container). Ideal pra canvases
 * (xyflow), editores tela-cheia, etc. Match por prefix do pathname.
 */
const FULL_BLEED_PREFIXES = ['/workflow', '/tasks', '/bpmn', '/connectors/sessions']

function isFullBleedRoute(pathname: string): boolean {
  return FULL_BLEED_PREFIXES.some((p) => pathname === p || pathname.startsWith(`${p}/`))
}

export function MainLayout() {
  const [collapsed, setCollapsed] = useState<boolean>(readCollapsed)
  const companyId = useActiveCompanyId()
  const location = useLocation()
  const fullBleed = isFullBleedRoute(location.pathname)

  // Listener global: usuário recebe toast de nova recommendation em qualquer
  // rota dentro do layout autenticado (VEC-409).
  useAthenaRecommendationsWs(companyId)

  const handleToggle = useCallback(() => {
    setCollapsed((prev) => {
      const next = !prev
      try {
        window.localStorage.setItem(SIDEBAR_STORAGE_KEY, String(next))
      } catch {
        // ignore
      }
      return next
    })
  }, [])

  return (
    <div className="flex min-h-screen bg-background">
      <Sidebar collapsed={collapsed} onToggleCollapse={handleToggle} />
      <div
        className={cn(
          'flex flex-1 flex-col transition-all duration-200',
          collapsed ? 'md:pl-16' : 'md:pl-64',
        )}
      >
        <Header />
        <DevRolePreviewBanner />
        <main
          className={cn(
            'flex flex-1 flex-col',
            fullBleed ? 'overflow-hidden p-0' : 'p-4 pb-20 md:p-8 md:pb-20',
          )}
        >
          {fullBleed ? (
            <Outlet />
          ) : (
            <div className="mx-auto w-full max-w-7xl">
              <Outlet />
            </div>
          )}
        </main>
      </div>
    </div>
  )
}
